import {useEffect, useState, type FocusEvent, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {Hint} from '@/components/ui/hint'
import {PorQue} from '@/components/ui/por-que'

const MAX_WORKERS = 1_000_000

function validateWorkerCount(count: number): string | null {
  return Number.isFinite(count) && count >= 1 && count <= MAX_WORKERS ? null : 'errors.workerCountInvalid'
}

export function WorkerCountDialog({
  open,
  onOpenChange,
  title,
  description,
  initial,
  pending,
  error,
  onSave,
  note,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  initial: number
  pending: boolean
  error: string | null
  onSave: (count: number) => void
  note?: (count: number) => string | null
}) {
  const {t} = useTranslation()
  const [count, setCount] = useState(initial)
  const [countError, setCountError] = useState<string | undefined>()
  // Ref de callback: Radix monta el contenido del diálogo después del primer efecto (`ui/popover.tsx`).
  const [nodo, setNodo] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (open) {
      setCount(initial)
      setCountError(undefined)
    }
  }, [open, initial])

  const valid = validateWorkerCount(count) === null
  const chapterNote = valid ? note?.(count) : null

  function blurCount(e: FocusEvent<HTMLInputElement>) {
    setCountError(validateWorkerCount(Number(e.target.value)) ?? undefined)
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const err = validateWorkerCount(count)
    setCountError(err ?? undefined)
    if (!err) onSave(count)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={setNodo}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="worker_count_edit">{t('workerCount.label')}</Label>
            <Input
              id="worker_count_edit"
              data-testid="worker-count-input"
              type="number"
              min={1}
              max={MAX_WORKERS}
              value={Number.isFinite(count) ? count : ''}
              onChange={(e) => {
                setCount(Number(e.target.value))
                setCountError(undefined)
              }}
              onBlur={blurCount}
              autoFocus
              aria-invalid={!!countError}
            />
            <FieldError error={countError} />
            {/* T2: el alcance queda visible; el porqué se pide (T7). */}
            <Hint testId="worker-count-scope">{t('workerCount.whyNote.scope')}</Hint>
          </div>
          {chapterNote && (
            <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="worker-count-chapter-note">
              {chapterNote}
            </p>
          )}
          <PorQue
            label={t('workerCount.whyNote.label')}
            titulo={t('workerCount.whyNote.titulo')}
            testId="worker-count-porque"
            contenidoTestId="worker-count-why-note"
            contenedor={nodo}>
            {t('workerCount.whyNote.explain')}
          </PorQue>
          <FormDialogFooter
            error={error}
            pending={pending || !valid}
            submitLabel={t('workerCount.save')}
            onCancel={() => onOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
